import config from '@/config';

import {
  mediaStoreBaseURL,
  PRODUCT_TYPE,
  staticMediaStoreBaseURL
} from '@/config/common';
import { formatToTwoDecimalString } from '@/lib/numberStringUtils';
import { isBrowser } from '@/lib/utils';
import { HOST_PORTAL_URL } from '@/services/connections.service';
import { COOKIE_MAP, getCookie } from '@/services/cookie.service';
import { getAgentInfo, getUserAuth } from '@/services/identity.service';

const defaultProductImage = `${staticMediaStoreBaseURL}/default-images/default-product.png`;

// TODO: Sidd - Cleanup - once the GA4 custom events are stable on production
const debugDataLayer = !config.isProduction;
function log(...arr) {
  // eslint-disable-next-line no-console
  debugDataLayer && console.log(...arr);
}
const recordAnalytics = Boolean(
  debugDataLayer ||
  process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ENABLE?.toLowerCase() === 'true'
);

const unAvailableValuePlaceholder = '';
const CURRENCY = 'AED';

const GTM_EVENTS = {
  ADD_DEFAULT_CART_SHIPPING_INFO: 'add_shipping_info',
  ADD_TO_CART: 'add_to_cart',
  BEGIN_DEFAULT_CART_CHECKOUT: 'begin_checkout',
  BROWSE_HOME_PAGE_CATEGORIES_CLICK: 'browse_categories_click',
  GENERIC_CTA_CLICK: 'cta_click',
  LEAD_RECEIVED: 'lead_received',
  LOGIN_EVENT: 'login_event',
  LOGOUT_EVENT: 'logout_event',
  NAV_BAR_CLICK: 'top_navigation_clicks',
  PAGE_VIEW: 'page_view_custom',
  PDP_VISIT: 'view_item',
  PRODUCT_CARD_ON_PLP_CLICK: 'select_item',
  PRODUCT_SEARCH_HIT_ENTER: 'user_search',
  REGISTER_PURCHASE_COMPLETION: 'purchase',
  REMOVE_FROM_CART: 'remove_from_cart',
  WHATSAPP_CLICK: 'whatsapp_clicked'
};

export const GTM_EVENTS_CTA_NAME = {
  BECOME_PARTNER: 'become_partner',
  BLOG_VIEW_ALL: 'blog_view_all',
  CONSULT_EVENT_EXPERT: 'consult_event_expert',
  CONTACT_US: 'contact_us',
  GALLERY_VIEW_ALL: 'gallery_view_all',
  PLAN_YOUR_EVENT: 'plan_your_event',
  WHATSAPP: 'whatsapp'
};

export const PAGE_NAME = {
  ACCEPTABLE_USE_POLICY: {
    label: 'ACCEPTABLE_USE_POLICY',
    regex: /^\/acceptable-use-policy/
  },
  CLP_AND_PLP: { label: 'CLP_AND_PLP', regex: /^\/categories\/./ },
  COLLECTION_HOME: {
    label: 'COLLECTION_HOME',
    regex: /^\/collections\/./
  },
  COLLECTION_LIST: { label: 'COLLECTION_LIST', regex: /^\/collections/ },
  CONTACT_US: { label: 'CONTACT_US', regex: /^\/contact-us/ },
  EVENT_LEAD_GEN: {
    label: 'EVENT_LEAD_GEN',
    regex: /^\/events\/lead-capture-form\/./
  },
  EVENT_MARKETING: { label: 'EVENT_MARKETING', regex: /^\/events\/./ },
  HOME_PAGE: { label: 'HOME_PAGE', regex: /^\/$/ },
  HOST_CART: { label: 'HOST_CART', regex: /^\/host-carts\/./ },
  MY_EVENTS: { label: 'MY_EVENTS', regex: /^\/my-events\/./ },
  PDP: { label: 'PDP', regex: /^\/products\/./ },
  PLANNER_CART: { label: 'PLANNER_CART', regex: /^\/planner-carts\/./ },
  PRIVACY_POLICY: { label: 'PRIVACY_POLICY', regex: /^\/privacy-policy/ },
  PRODUCT_SEARCH_RESULT_LIST: {
    label: 'PRODUCT_SEARCH_RESULT_LIST',
    regex: /^\/search-result\/./
  },
  QUOTE: { label: 'QUOTE', regex: /^\/quotes\/./ },
  REGISTER_AS_PARTNER: {
    label: 'REGISTER_AS_PARTNER',
    regex: /^\/partners\/(basic-info|lead-capture-form|business-location-info)/g
  },
  TERMS_AND_CONDITION: {
    label: 'TERMS_AND_CONDITION',
    regex: /^\/terms-and-conditions/
  },
  USER_PROFILE_GET_HELP: {
    label: 'USER_PROFILE_GET_HELP',
    regex: /^\/user-profile\/get-help/
  }
};

const getGoogleClientId = () => {
  const gaCookie = getCookie({ name: COOKIE_MAP.GA }) || '';
  return gaCookie.substring(6);
};

const getAllUserIds = () => {
  const userAuth = getUserAuth();
  const opsUserInfo = getAgentInfo();
  const plannerId = opsUserInfo?.agent?.id;
  let hostId;
  let guestId;
  if (plannerId) {
    hostId = opsUserInfo.id;
  } else {
    hostId = userAuth?.isGuest ? null : userAuth?.id; // TODO: Sidd - check if some common identity function can be used here
    guestId = userAuth?.isGuest ? userAuth.id : null; // TODO: Sidd - check if some common identity function can be used here
  }
  return {
    client_id: getGoogleClientId(),
    user_id: hostId,
    planner_id: plannerId,
    guest_id: guestId
  };
};

const convertToFloatValue = ({ value }) =>
  parseFloat(formatToTwoDecimalString({ value }));

const captureEvent = (eventObject = {}) => {
  const eventObjectWithIds = {
    ...getAllUserIds(),
    ...eventObject
  };
  if (isBrowser() && recordAnalytics) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(eventObjectWithIds);
  }
};

const getPageName = (urlPathQueryFragment = '') => {
  const pageNameObjectList = Object.values(PAGE_NAME);
  const urlPath = new URL(`${HOST_PORTAL_URL}${urlPathQueryFragment}`).pathname;
  const pageNameObject = pageNameObjectList.find(({ regex }) =>
    regex.test(urlPath)
  );
  return pageNameObject?.label || 'unknown';
};

export const captureGTMEventPageView = (urlPathQueryFragment) => {
  log('captureGTMEventPageView', {
    event: GTM_EVENTS.PAGE_VIEW,
    page: urlPathQueryFragment,
    page_name: getPageName(urlPathQueryFragment)
  });
  captureEvent({
    event: GTM_EVENTS.PAGE_VIEW,
    page: urlPathQueryFragment,
    page_name: getPageName(urlPathQueryFragment)
  });
};

export const captureGTMEventCTAClick = ({ ctaName, pageName, path }) => {
  captureEvent({
    event: GTM_EVENTS.GENERIC_CTA_CLICK,
    cta_click: ctaName,
    page_name: pageName || getPageName(path)
  });
};

const cartAndOrderItemDefaultValues = {
  affiliation: 'Website',
  coupon: unAvailableValuePlaceholder,
  discount: 0,
  index: 0,
  item_brand: unAvailableValuePlaceholder,
  item_id: 'item-id-not-available',
  item_list_id: unAvailableValuePlaceholder,
  item_name: 'item-name-not-available',
  item_variant: unAvailableValuePlaceholder,
  location_id: unAvailableValuePlaceholder,
  price: 0,
  quantity: 1
};

const assignedToCategoriesWrapper = (assignedToCategories) => {
  if (!assignedToCategories || assignedToCategories.length === 0) return {};
  return {
    item_category: assignedToCategories[0]
      ? assignedToCategories[0].toLowerCase()
      : null,
    item_category2: assignedToCategories[1]
      ? assignedToCategories[1].toLowerCase()
      : null,
    item_category3: assignedToCategories[2]
      ? assignedToCategories[2].toLowerCase()
      : null,
    item_list_name: assignedToCategories[0]
      ? assignedToCategories[0].toLowerCase()
      : null
  };
};

const getFirstItemImageURL = (productMedia) => {
  const [{ media: { url: productMediaUrl } = {} } = {}] = productMedia || [];
  return productMediaUrl
    ? `${mediaStoreBaseURL}/${productMediaUrl}`
    : defaultProductImage;
};

const categoryWrapper = (categories) => {
  if (!categories) return {};
  const category = categories[0];

  return {
    item_category: category?.name.toLowerCase() || null,
    item_category2: category?.parentCategory?.name.toLowerCase() || null,
    item_category3:
      category?.parentCategory?.parentCategory?.name.toLowerCase() || null,
    item_list_name: category?.name.toLowerCase() || null
  };
};

const checkoutAndPurchasedOrderItems = (orderItems) =>
  orderItems.map(
    (
      { unitListedDiscount, quantity, entityId, product, sellingPrice },
      index
    ) => {
      const { categories = [], productMedia = [] } = product || {};

      const discountedPrice = unitListedDiscount
        ? convertToFloatValue({
            value: (unitListedDiscount || 0) * quantity
          })
        : 0;

      return {
        ...cartAndOrderItemDefaultValues,
        ...categoryWrapper(categories),
        discount: discountedPrice,
        index,
        item_id: entityId,
        item_name: product.name,
        item_image_url: getFirstItemImageURL(productMedia),
        price: convertToFloatValue({ value: sellingPrice }),
        quantity
      };
    }
  );

const checkoutAndPurchasedCartItems = (cartItems) =>
  cartItems
    .filter(({ derivedValues: { isValidForCheckout } }) => isValidForCheckout)
    .map(
      (
        {
          id,
          derivedValues: { unitListedPrice, unitListedDiscount },
          product: { categories, name, productMedia = [] },
          quantity
        },
        index
      ) => {
        const discountedPrice = unitListedDiscount
          ? convertToFloatValue({
              value: (unitListedDiscount || 0) * quantity
            })
          : 0;
        return {
          ...cartAndOrderItemDefaultValues,
          ...categoryWrapper(categories),
          discount: discountedPrice,
          item_id: id,
          index,
          item_name: name,
          price: convertToFloatValue({ value: unitListedPrice || 0 }),
          quantity,
          item_image_url: getFirstItemImageURL(productMedia)
        };
      }
    );

export const captureGTMEventPurchaseCompletion = ({
  orderId,
  orderItems = [],
  orderNumber,
  orderTotal,
  paymentFlowSource
}) => {
  const eCommerce = {
    order_number: orderNumber,
    currency: CURRENCY,
    items:
      orderItems.length > 0
        ? checkoutAndPurchasedOrderItems(orderItems)
        : [
            {
              ...cartAndOrderItemDefaultValues,
              price: convertToFloatValue({ value: orderTotal }),
              item_image_url: defaultProductImage
            }
          ],
    transaction_id: orderId,
    value: convertToFloatValue({ value: orderTotal })
  };
  captureEvent({
    event: GTM_EVENTS.REGISTER_PURCHASE_COMPLETION,
    ecommerce: eCommerce,
    payment_flow_source: paymentFlowSource
  });
};

export const captureGTMEventBrowseCategoriesClick = ({
  name: categoryName
}) => {
  captureEvent({
    category: categoryName,
    event: GTM_EVENTS.BROWSE_HOME_PAGE_CATEGORIES_CLICK,
    page_name: PAGE_NAME.HOME_PAGE.label
  });
};

const calculatePriceDiscountAndValue = ({ discountValue, price, quantity }) => {
  const discountedPrecentage = convertToFloatValue({ value: discountValue });
  const priceValue = convertToFloatValue({ value: price });
  const discountedPrice =
    ((priceValue * discountedPrecentage) / 100) * quantity;
  const value = priceValue * quantity - discountedPrice;
  return { discountedPrice, priceValue, value };
};

export const captureGTMEventSelectItem = ({
  assignedToCategories,
  availableDiscount,
  id,
  name,
  price,
  quantity,
  type
}) => {
  const productPrice = {
    [PRODUCT_TYPE.CART]: price,
    [PRODUCT_TYPE.QUOTE]: 0
  };

  const { discountedPrice, priceValue } = calculatePriceDiscountAndValue({
    discountValue: availableDiscount?.value || 0,
    price: productPrice[type],
    quantity: 1
  });

  const ecommerce = {
    items: [
      {
        ...cartAndOrderItemDefaultValues,
        ...assignedToCategoriesWrapper(assignedToCategories),
        discount: discountedPrice,
        index: 0,
        item_id: id,
        item_name: name,
        price: priceValue,
        quantity
      }
    ]
  };
  captureEvent({ event: GTM_EVENTS.PRODUCT_CARD_ON_PLP_CLICK, ecommerce });
};

export const captureGTMEventViewItem = ({
  availableDiscount,
  categories,
  id,
  name,
  price,
  productMedia,
  type
}) => {
  const productPrice = {
    [PRODUCT_TYPE.CART]: price,
    [PRODUCT_TYPE.QUOTE]: 0
  };

  const { discountedPrice, priceValue } = calculatePriceDiscountAndValue({
    discountValue: availableDiscount?.value || 0,
    price: productPrice[type],
    quantity: 1
  });

  const ecommerce = {
    currency: CURRENCY,
    value: priceValue,
    items: [
      {
        ...cartAndOrderItemDefaultValues,
        ...categoryWrapper(categories),
        discount: discountedPrice,
        index: 0,
        item_id: id,
        item_name: name,
        price: priceValue,
        item_image_url: getFirstItemImageURL(productMedia)
      }
    ]
  };
  captureEvent({ event: GTM_EVENTS.PDP_VISIT, ecommerce });
};

export const captureGTMEventAddToCart = ({
  availableDiscount,
  assignedToCategories,
  categories,
  id,
  name,
  pageName,
  price,
  productMedia,
  quantity,
  type = PRODUCT_TYPE.CART
}) => {
  const productPrice = {
    [PRODUCT_TYPE.CART]: price,
    [PRODUCT_TYPE.QUOTE]: 0
  };
  const { discountedPrice, priceValue, value } = calculatePriceDiscountAndValue(
    {
      discountValue: availableDiscount?.value || 0,
      price: productPrice[type],
      quantity
    }
  );

  const ecommerce = {
    currency: CURRENCY,
    value,
    items: [
      {
        ...cartAndOrderItemDefaultValues,
        ...categoryWrapper(categories),
        ...assignedToCategoriesWrapper(assignedToCategories),
        coupon: unAvailableValuePlaceholder,
        discount: discountedPrice,
        index: 0,
        item_id: id,
        item_name: name,
        price: priceValue,
        item_image_url: getFirstItemImageURL(productMedia),
        quantity
      }
    ]
  };
  captureEvent({
    event: GTM_EVENTS.ADD_TO_CART,
    ecommerce,
    page_name: pageName
  });
};

export const captureGTMEventRemoveFromCart = ({
  availableDiscount,
  categories,
  id,
  name,
  pageName,
  price,
  quantity,
  type
}) => {
  const productPrice = {
    [PRODUCT_TYPE.CART]: price,
    [PRODUCT_TYPE.QUOTE]: 0
  };
  const { discountedPrice, priceValue, value } = calculatePriceDiscountAndValue(
    {
      discountValue: availableDiscount?.value || 0,
      price: productPrice[type],
      quantity
    }
  );

  const ecommerce = {
    currency: CURRENCY,
    value,
    items: [
      {
        ...cartAndOrderItemDefaultValues,
        ...categoryWrapper(categories),
        discount: discountedPrice,
        index: 0,
        item_id: id,
        item_name: name,
        price: priceValue,
        quantity
      }
    ]
  };

  captureEvent({
    event: GTM_EVENTS.REMOVE_FROM_CART,
    ecommerce,
    page_name: pageName
  });
};

export const captureGTMEventSuccessfullLeadForm = () => {
  captureEvent({ event: GTM_EVENTS.LEAD_RECEIVED });
};

export const captureGTMEventWhatsappClick = () => {
  captureEvent({ event: GTM_EVENTS.WHATSAPP_CLICK });
};

export const captureGTMEventLogin = ({
  mobile,
  status,
  userId: userIdToOverrideAsNotYetSetInCookies
}) => {
  captureEvent({
    event: GTM_EVENTS.LOGIN_EVENT,
    method: 'OTP login',
    status,
    user_id: userIdToOverrideAsNotYetSetInCookies,
    user_mobile: mobile
  });
};

export const captureGTMEventLogOut = () => {
  captureEvent({ event: GTM_EVENTS.LOGOUT_EVENT, status: true });
};

export const captureGTMEventNavBarClick = ({
  category: { name },
  childCategory,
  path
}) => {
  captureEvent({
    category: name,
    event: GTM_EVENTS.NAV_BAR_CLICK,
    page_name: getPageName(path),
    sub_category: childCategory
  });
};

export const captureGTMEventBeginCheckout = ({
  code = unAvailableValuePlaceholder,
  itemsList,
  orderTotal,
  pageName
}) => {
  const ecommerce = {
    currency: CURRENCY,
    value: convertToFloatValue({ value: orderTotal }),
    coupon: code || unAvailableValuePlaceholder,
    items: checkoutAndPurchasedCartItems(itemsList)
  };
  captureEvent({
    event: GTM_EVENTS.BEGIN_DEFAULT_CART_CHECKOUT,
    pageName,
    ecommerce
  });
};

export const captureGTMEventAddShippingInfo = ({
  code = unAvailableValuePlaceholder,
  itemList,
  orderTotal
}) => {
  const ecommerce = {
    currency: CURRENCY,
    value: convertToFloatValue({ value: orderTotal }),
    coupon: code || unAvailableValuePlaceholder,
    shipping_tier: 'Ground',
    items: checkoutAndPurchasedCartItems(itemList)
  };
  captureEvent({ event: GTM_EVENTS.ADD_DEFAULT_CART_SHIPPING_INFO, ecommerce });
};

export const updateGTMEventCartQuantity = ({
  availableDiscount,
  currentQuantity,
  id,
  minPrice,
  name,
  oldQuantity,
  pageName,
  price,
  type
}) => {
  const isQuantityChanged = oldQuantity !== currentQuantity;

  if (isQuantityChanged) {
    const isQuantityIncreased = oldQuantity < currentQuantity;
    const captureGTMEventClick = isQuantityIncreased
      ? captureGTMEventAddToCart
      : captureGTMEventRemoveFromCart;

    captureGTMEventClick({
      availableDiscount,
      id,
      minPrice,
      name,
      pageName,
      price,
      quantity: currentQuantity,
      type
    });
  }
};
